import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Save, RefreshCw, MessageSquare, Info, ChevronDown, ChevronUp,
  Send, ArrowUp, ArrowDown, X, Plus, ToggleLeft, ToggleRight,
  Mail, Bell, Smartphone, AlertTriangle, CheckCircle2, Eye, Pencil
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AdminNotificationTemplates() {
  const [templates, setTemplates] = useState({});
  const [variableRegistry, setVariableRegistry] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('Test User');
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [editingVar, setEditingVar] = useState(null); // {templateKey, index}
  const [activeTab, setActiveTab] = useState({}); // per-template: 'whatsapp' | 'email'
  const [quickTestKey, setQuickTestKey] = useState('booking_confirmation');

  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/notification-templates`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || {});
        setVariableRegistry(data.variable_registry || {});
      }
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const updateTemplate = (key, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  };

  const moveVariable = (templateKey, index, direction) => {
    setTemplates(prev => {
      const tmpl = { ...prev[templateKey] };
      const vars = [...(tmpl.variable_map || [])];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= vars.length) return prev;
      [vars[index], vars[newIndex]] = [vars[newIndex], vars[index]];
      return { ...prev, [templateKey]: { ...tmpl, variable_map: vars } };
    });
  };

  const removeVariable = (templateKey, index) => {
    setTemplates(prev => {
      const tmpl = { ...prev[templateKey] };
      const vars = [...(tmpl.variable_map || [])];
      vars.splice(index, 1);
      return { ...prev, [templateKey]: { ...tmpl, variable_map: vars } };
    });
    setEditingVar(null);
  };

  const addVariable = (templateKey, varName) => {
    setTemplates(prev => {
      const tmpl = { ...prev[templateKey] };
      const vars = [...(tmpl.variable_map || [])];
      if (!vars.includes(varName)) vars.push(varName);
      return { ...prev, [templateKey]: { ...tmpl, variable_map: vars } };
    });
  };

  const changeVariable = (templateKey, index, newVarName) => {
    setTemplates(prev => {
      const tmpl = { ...prev[templateKey] };
      const vars = [...(tmpl.variable_map || [])];
      // If the new variable already exists at another position, swap them
      const existingIdx = vars.indexOf(newVarName);
      if (existingIdx !== -1 && existingIdx !== index) {
        vars[existingIdx] = vars[index];
      }
      vars[index] = newVarName;
      return { ...prev, [templateKey]: { ...tmpl, variable_map: vars } };
    });
    setEditingVar(null);
  };

  const saveTemplates = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/notification-templates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templates })
      });
      if (res.ok) {
        toast.success('All templates saved!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Save failed');
      }
    } catch {
      toast.error('Save error');
    } finally {
      setSaving(false);
    }
  };

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('admin_token') || ''}`,
  });

  const runTest = async (key, channels) => {
    if (channels.includes('whatsapp') && !testPhone) { toast.error('Enter a phone number'); return; }
    if (channels.includes('email') && !testEmail) { toast.error('Enter an email address'); return; }
    const testKey = `${channels.join('+')}-${key}`;
    setTesting(testKey);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/notification-templates/test`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          template_key: key,
          phone: testPhone,
          email: testEmail,
          name: testName || 'Test User',
          channels,
        }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Test failed'); return; }
      setTestResults(prev => ({ ...prev, [testKey]: data }));
      const wa = data.results?.whatsapp, em = data.results?.email;
      const parts = [];
      if (channels.includes('whatsapp')) parts.push(wa?.ok ? '✅ WhatsApp sent' : `❌ WhatsApp: ${wa?.detail?.error || wa?.detail?.message || 'failed'}`);
      if (channels.includes('email')) parts.push(em?.ok ? '✅ Email sent' : `❌ Email: ${em?.detail?.error || 'failed'}`);
      const allOk = (channels.includes('whatsapp') ? wa?.ok : true) && (channels.includes('email') ? em?.ok : true);
      (allOk ? toast.success : toast.error)(parts.join(' · '));
    } catch (e) { toast.error('Test failed: ' + e.message); }
    finally { setTesting(null); }
  };

  const testWhatsApp = (key) => runTest(key, ['whatsapp']);
  const testEmailSend = (key) => runTest(key, ['email']);
  const testBoth = (key) => runTest(key, ['whatsapp', 'email']);

  const getSampleValue = (varName) => {
    const samples = {
      customer_name: 'Rajesh Kumar', order_id: 'RD-10045', order_total: 'Rs.4,999',
      support_phone: '+91 9887068888', payment_method: 'Razorpay',
      delivery_address: 'Main Tonk Road, Jaipur', item_count: '2',
      product_names: 'Kent Grand Plus RO', booking_id: 'BK-00A1',
      service_type: 'RO Annual Maintenance', customer_phone: '9887068888',
      status_text: 'Confirmed', tracking_number: 'TRACK123456',
      technician_name: 'Rajesh Kumar', scheduled_date: 'Tomorrow 10 AM',
      refund_amount: 'Rs.4,999', customer_address: 'Partap Nagar, Jaipur'
    };
    return samples[varName] || varName;
  };

  const resolveSubjectPreview = (subject) => {
    if (!subject) return '';
    let resolved = subject;
    Object.entries({
      customer_name: 'Rajesh Kumar', order_id: 'RD-10045', order_total: 'Rs.4,999',
      support_phone: '+91 9887068888', booking_id: 'BK-00A1',
      service_type: 'RO Annual Maintenance', status_text: 'Confirmed',
      refund_amount: 'Rs.4,999', customer_phone: '9887068888',
      payment_method: 'Razorpay', product_names: 'Kent Grand Plus RO',
      customer_address: 'Partap Nagar, Jaipur'
    }).forEach(([k, v]) => { resolved = resolved.replace(new RegExp(`\\{${k}\\}`, 'g'), v); });
    return resolved;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const templateGroups = {
    'Customer Notifications': ['order_confirmation', 'order_status_update', 'booking_confirmation', 'booking_status_update', 'refund_notification', 'welcome_message'],
    'Admin Alerts': ['admin_new_order', 'admin_new_booking']
  };

  const groupIcons = {
    'Customer Notifications': <Smartphone size={18} className="text-blue-600" />,
    'Admin Alerts': <Bell size={18} className="text-orange-600" />
  };

  return (
    <div data-testid="admin-notification-templates" className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="templates-title">Notification Templates</h1>
          <p className="text-gray-500 text-sm mt-1">Manage WhatsApp &amp; Email templates with full variable control</p>
        </div>
        <button onClick={saveTemplates} disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-semibold shadow-sm"
          data-testid="save-templates-btn">
          {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
          Save All
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
        <div className="flex gap-3">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">How Variable Mapping Works</p>
            <p className="text-amber-700 leading-relaxed">
              <strong>WhatsApp:</strong> Variables map to <code className="bg-amber-100 px-1 rounded">{'{{1}}'}</code>, <code className="bg-amber-100 px-1 rounded">{'{{2}}'}</code>, etc. Click the <Pencil size={10} className="inline" /> icon to change any variable inline.
              <br /><strong>Email:</strong> Use <code className="bg-amber-100 px-1 rounded">{'{variable_name}'}</code> in subject &amp; greeting (e.g., <code className="bg-amber-100 px-1 rounded">{'{customer_name}'}</code>).
            </p>
          </div>
        </div>
      </div>

      {/* Test Panel */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6" data-testid="global-test-panel">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Send size={14} /> Send Test Notification
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Test Phone (with country code)</label>
            <input type="tel" value={testPhone} onChange={e => setTestPhone(e.target.value)}
              placeholder="919887068888" data-testid="test-phone-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Test Email</label>
            <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
              placeholder="test@example.com" data-testid="test-email-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Test Customer Name</label>
            <input type="text" value={testName} onChange={e => setTestName(e.target.value)}
              placeholder="Test User" data-testid="test-name-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Template to test</label>
            <select value={quickTestKey} onChange={e => setQuickTestKey(e.target.value)}
              data-testid="quick-test-template"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
              {Object.keys(templates).map(k => (
                <option key={k} value={k}>{k} → {templates[k]?.campaign_name || '(no campaign)'}</option>
              ))}
            </select>
          </div>
          <button onClick={() => runTest(quickTestKey, ['whatsapp'])}
            disabled={!quickTestKey || testing !== null}
            data-testid="quick-test-whatsapp"
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold inline-flex items-center gap-1.5">
            <MessageSquare size={14} /> WhatsApp Only
          </button>
          <button onClick={() => runTest(quickTestKey, ['email'])}
            disabled={!quickTestKey || testing !== null}
            data-testid="quick-test-email"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold inline-flex items-center gap-1.5">
            <Mail size={14} /> Email Only
          </button>
          <button onClick={() => runTest(quickTestKey, ['whatsapp', 'email'])}
            disabled={!quickTestKey || testing !== null}
            data-testid="quick-test-both"
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 shadow-md">
            <Send size={14} /> {testing ? 'Sending...' : 'Send Both'}
          </button>
        </div>
        {quickTestKey && testResults[`whatsapp+email-${quickTestKey}`] && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs font-mono text-gray-700 overflow-auto max-h-40">
            <pre>{JSON.stringify(testResults[`whatsapp+email-${quickTestKey}`], null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Template Groups */}
      {Object.entries(templateGroups).map(([group, keys]) => (
        <div key={group} className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            {groupIcons[group]} {group}
          </h2>
          <div className="space-y-3">
            {keys.map(key => {
              const tmpl = templates[key] || {};
              const registry = variableRegistry[key] || {};
              const isExpanded = expandedTemplate === key;
              const variableMap = tmpl.variable_map || [];
              const availableVars = registry.available_variables || {};
              const allVarKeys = Object.keys(availableVars);
              const unusedVars = allVarKeys.filter(v => !variableMap.includes(v));
              const tab = activeTab[key] || 'whatsapp';

              return (
                <div key={key}
                  className={`bg-white rounded-xl shadow-sm border transition-all ${isExpanded ? 'ring-2 ring-blue-200' : ''}`}
                  data-testid={`template-${key}`}>

                  {/* Template Header */}
                  <div className="p-4 flex items-start gap-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${tmpl.enabled || tmpl.email_enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <MessageSquare size={18} className={tmpl.enabled || tmpl.email_enabled ? 'text-green-700' : 'text-gray-400'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-gray-900 capitalize">{key.replace(/_/g, ' ')}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${registry.recipient === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                          {registry.recipient || 'customer'}
                        </span>
                        {/* Channel badges */}
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${tmpl.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                          WA {tmpl.enabled ? 'ON' : 'OFF'}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${tmpl.email_enabled ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                          Email {tmpl.email_enabled ? 'ON' : 'OFF'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{tmpl.description || registry.description}</p>
                    </div>
                    <button onClick={() => setExpandedTemplate(isExpanded ? null : key)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 flex-shrink-0"
                      data-testid={`expand-${key}`}>
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>

                  {/* Expanded Panel */}
                  {isExpanded && (
                    <div className="border-t">
                      {/* Tab Switcher */}
                      <div className="flex border-b bg-gray-50">
                        <button onClick={() => setActiveTab(p => ({ ...p, [key]: 'whatsapp' }))}
                          className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'whatsapp' ? 'border-green-600 text-green-700 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                          data-testid={`tab-whatsapp-${key}`}>
                          <MessageSquare size={14} /> WhatsApp
                        </button>
                        <button onClick={() => setActiveTab(p => ({ ...p, [key]: 'email' }))}
                          className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'email' ? 'border-blue-600 text-blue-700 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                          data-testid={`tab-email-${key}`}>
                          <Mail size={14} /> Email
                        </button>
                      </div>

                      {/* WhatsApp Tab */}
                      {tab === 'whatsapp' && (
                        <div className="p-4 space-y-4">
                          {/* Enable Toggle + Campaign Name */}
                          <div className="flex items-center gap-4">
                            <button onClick={() => updateTemplate(key, 'enabled', !tmpl.enabled)}
                              className={`flex items-center gap-2 text-sm font-medium ${tmpl.enabled ? 'text-green-600' : 'text-gray-400'}`}
                              data-testid={`toggle-wa-${key}`}>
                              {tmpl.enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                              {tmpl.enabled ? 'Enabled' : 'Disabled'}
                            </button>
                            <div className="flex-1">
                              <label className="block text-xs font-medium text-gray-500 mb-1">AiSensy Campaign Name</label>
                              <input type="text" value={tmpl.campaign_name || ''}
                                onChange={e => updateTemplate(key, 'campaign_name', e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                                placeholder="e.g. order_confirmation" data-testid={`campaign-name-${key}`} />
                            </div>
                          </div>

                          {/* Variable Mapping */}
                          <div>
                            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                              <span className="w-4 h-4 bg-green-600 text-white rounded text-[10px] flex items-center justify-center font-bold">V</span>
                              Variable Mapping (Position = Data Field)
                            </h4>
                            {variableMap.length === 0 ? (
                              <p className="text-xs text-gray-400 italic py-2">No variables mapped. Add below.</p>
                            ) : (
                              <div className="space-y-1.5">
                                {variableMap.map((varName, idx) => {
                                  const isEditing = editingVar?.templateKey === key && editingVar?.index === idx;
                                  return (
                                    <div key={`${key}-${idx}`}
                                      className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2"
                                      data-testid={`var-row-${key}-${idx}`}>
                                      <span className="w-8 h-8 bg-green-600 text-white rounded-lg text-xs font-bold flex items-center justify-center flex-shrink-0">
                                        {`{{${idx + 1}}}`}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        {isEditing ? (
                                          <select value={varName}
                                            onChange={e => changeVariable(key, idx, e.target.value)}
                                            onBlur={() => setEditingVar(null)}
                                            autoFocus
                                            className="w-full px-2 py-1 border border-blue-400 rounded text-sm bg-blue-50 focus:ring-2 focus:ring-blue-500"
                                            data-testid={`var-select-${key}-${idx}`}>
                                            {allVarKeys.map(v => (
                                              <option key={v} value={v}>{v.replace(/_/g, ' ')} — {availableVars[v]}</option>
                                            ))}
                                          </select>
                                        ) : (
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-sm font-medium text-gray-800">{varName.replace(/_/g, ' ')}</span>
                                            <span className="text-xs text-gray-400">= "{getSampleValue(varName)}"</span>
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-0.5 flex-shrink-0">
                                        <button onClick={() => setEditingVar({ templateKey: key, index: idx })}
                                          className="p-1 hover:bg-blue-50 text-blue-400 hover:text-blue-600 rounded"
                                          title="Change variable" data-testid={`edit-var-${key}-${idx}`}>
                                          <Pencil size={13} />
                                        </button>
                                        <button onClick={() => moveVariable(key, idx, -1)} disabled={idx === 0}
                                          className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                                          data-testid={`move-up-${key}-${idx}`}>
                                          <ArrowUp size={13} />
                                        </button>
                                        <button onClick={() => moveVariable(key, idx, 1)} disabled={idx === variableMap.length - 1}
                                          className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                                          data-testid={`move-down-${key}-${idx}`}>
                                          <ArrowDown size={13} />
                                        </button>
                                        <button onClick={() => removeVariable(key, idx)}
                                          className="p-1 hover:bg-red-50 text-red-400 hover:text-red-600 rounded"
                                          data-testid={`remove-var-${key}-${idx}`}>
                                          <X size={13} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Available Variables */}
                          {unusedVars.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 mb-2">Add Variable</h4>
                              <div className="flex flex-wrap gap-1.5">
                                {unusedVars.map(v => (
                                  <button key={v} onClick={() => addVariable(key, v)}
                                    className="flex items-center gap-1 px-2.5 py-1 bg-white border border-dashed border-gray-300 rounded-lg text-xs text-gray-600 hover:border-green-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                                    data-testid={`add-var-${key}-${v}`}>
                                    <Plus size={12} /> {v.replace(/_/g, ' ')}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* WA Preview */}
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <h4 className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1"><Eye size={12} /> Preview</h4>
                            <p className="text-sm text-green-800 font-mono">
                              {tmpl.sample_template || (variableMap.length > 0
                                ? variableMap.map((v, i) => `{{${i + 1}}} = ${getSampleValue(v)}`).join('  |  ')
                                : 'Map variables above to see preview')}
                            </p>
                          </div>

                          {/* Template Text */}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Template Body (admin reference)</label>
                            <textarea value={tmpl.sample_template || ''} rows={2}
                              onChange={e => updateTemplate(key, 'sample_template', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 font-mono"
                              placeholder={`e.g. Hi {{1}}, your order #{{2}} for {{3}} is confirmed! Call {{4}}`}
                              data-testid={`sample-template-${key}`} />
                          </div>

                          {/* WA Test */}
                          <div className="flex items-center gap-3">
                            <button onClick={() => testWhatsApp(key)} disabled={testing === `wa-${key}` || !testPhone}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
                              data-testid={`test-wa-${key}`}>
                              {testing === `wa-${key}` ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                              Test WhatsApp
                            </button>
                            {testResults[`wa-${key}`] && (
                              <span className={`text-xs font-medium ${testResults[`wa-${key}`].result?.status === 'sent' ? 'text-green-600' : 'text-red-500'}`}>
                                {testResults[`wa-${key}`].result?.status === 'sent'
                                  ? <><CheckCircle2 size={14} className="inline mr-1" />Sent</>
                                  : <><AlertTriangle size={14} className="inline mr-1" />{testResults[`wa-${key}`].result?.error || testResults[`wa-${key}`].result?.reason}</>}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Email Tab */}
                      {tab === 'email' && (
                        <div className="p-4 space-y-4">
                          {/* Enable Toggle */}
                          <button onClick={() => updateTemplate(key, 'email_enabled', !tmpl.email_enabled)}
                            className={`flex items-center gap-2 text-sm font-medium ${tmpl.email_enabled ? 'text-blue-600' : 'text-gray-400'}`}
                            data-testid={`toggle-email-${key}`}>
                            {tmpl.email_enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                            {tmpl.email_enabled ? 'Email Enabled' : 'Email Disabled'}
                          </button>

                          {/* Email Subject */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Email Subject
                              <span className="text-gray-400 font-normal ml-1">Use {'{variable_name}'} for dynamic values</span>
                            </label>
                            <input type="text" value={tmpl.email_subject || ''}
                              onChange={e => updateTemplate(key, 'email_subject', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                              placeholder={registry.email?.default_subject || 'Email subject...'}
                              data-testid={`email-subject-${key}`} />
                            {tmpl.email_subject && (
                              <p className="text-xs text-gray-400 mt-1">
                                Preview: <span className="text-gray-600 font-medium">{resolveSubjectPreview(tmpl.email_subject)}</span>
                              </p>
                            )}
                          </div>

                          {/* Email Greeting */}
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Email Greeting / Message
                              <span className="text-gray-400 font-normal ml-1">Use {'{variable_name}'} for dynamic values</span>
                            </label>
                            <textarea value={tmpl.email_greeting || ''} rows={2}
                              onChange={e => updateTemplate(key, 'email_greeting', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                              placeholder={registry.email?.default_greeting || 'Greeting text...'}
                              data-testid={`email-greeting-${key}`} />
                            {tmpl.email_greeting && (
                              <p className="text-xs text-gray-400 mt-1">
                                Preview: <span className="text-gray-600">{resolveSubjectPreview(tmpl.email_greeting)}</span>
                              </p>
                            )}
                          </div>

                          {/* Available Variables Reference */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <h4 className="text-xs font-semibold text-blue-700 mb-2">Available Variables for Subject &amp; Greeting</h4>
                            <div className="flex flex-wrap gap-1.5">
                              {allVarKeys.map(v => (
                                <span key={v} className="inline-flex items-center px-2 py-0.5 bg-white border border-blue-200 rounded text-xs text-blue-700 font-mono">
                                  {`{${v}}`}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Email Preview */}
                          <div className="bg-gray-50 border rounded-lg p-3">
                            <h4 className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1"><Eye size={12} /> Email Preview</h4>
                            <div className="bg-white rounded border p-3 text-sm">
                              <p className="font-semibold text-gray-900 mb-1">
                                Subject: {resolveSubjectPreview(tmpl.email_subject || registry.email?.default_subject || '')}
                              </p>
                              <hr className="my-2" />
                              <p className="text-gray-700">
                                Hi <strong>{testName || 'Customer'}</strong>,
                              </p>
                              <p className="text-gray-600 mt-1">
                                {resolveSubjectPreview(tmpl.email_greeting || registry.email?.default_greeting || '')}
                              </p>
                            </div>
                          </div>

                          {/* Email Test */}
                          <div className="flex items-center gap-3">
                            <button onClick={() => testEmailSend(key)} disabled={testing === `em-${key}` || !testEmail}
                              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                              data-testid={`test-email-${key}`}>
                              {testing === `em-${key}` ? <RefreshCw size={14} className="animate-spin" /> : <Mail size={14} />}
                              Test Email
                            </button>
                            {testResults[`em-${key}`] && (
                              <span className={`text-xs font-medium ${testResults[`em-${key}`].result?.success ? 'text-green-600' : 'text-red-500'}`}>
                                {testResults[`em-${key}`].result?.success
                                  ? <><CheckCircle2 size={14} className="inline mr-1" />Sent!</>
                                  : <><AlertTriangle size={14} className="inline mr-1" />{testResults[`em-${key}`].result?.error}</>}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Setup Guide */}
      <div className="mt-8 bg-white rounded-xl shadow-sm border p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Info size={16} className="text-blue-600" /> Setup Guide
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
          <div>
            <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-1"><MessageSquare size={14} /> WhatsApp (AiSensy)</h4>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Create templates in <a href="https://app.aisensy.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">AiSensy</a> with {'{{1}}'}, {'{{2}}'} placeholders</li>
              <li>Create API campaigns, set campaign name</li>
              <li>Enter campaign name above, map variables to match template order</li>
              <li>Click <Pencil size={10} className="inline" /> to change any variable at a position</li>
            </ol>
          </div>
          <div>
            <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-1"><Mail size={14} /> Email (Brevo)</h4>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Email templates use branded HTML (auto-generated)</li>
              <li>Customize Subject and Greeting with {'{variable}'} placeholders</li>
              <li>Toggle ON/OFF per notification type</li>
              <li>Test with the "Test Email" button</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminNotificationTemplates;
